/** @file Create-bone tool: press = head, release = tail (snapped); bone.create under the gizmo bone or root. */
import { POSE_LAYOUT } from '@pose2d/core/contracts/pose';
import { boneFromPoints } from '@pose2d/editor-geometry/boneFromPoints';
import { viewCamera } from '@pose2d/editor-geometry/viewCamera';
import { slugifyName } from '@pose2d/format/slugifyName';
import type { Vec2 } from '@pose2d/math/types';
import type { ToolContext, ToolPointer } from '../../panels/viewport/viewportTypes';
import { worldAt } from '../../panels/viewport/worldAt';
import type { PendingCommand } from '../../stores/types';
import { updateSelection } from '../../stores/updateSelection';
import { roundValue } from '../transform/roundValue';
import { snappedWorld } from '../transform/snappedWorld';
import { topSelected } from '../transform/topSelected';

/** The bone.create of a new bone `name` from head to tail under data bone `parent`; no client id (W3-11). */
function createCall(ctx: ToolContext, parent: number, name: string, ends: [Vec2, Vec2]): PendingCommand {
  const W = POSE_LAYOUT.WORLD_STRIDE;
  const b = boneFromPoints(ctx.pose.world.slice(parent * W, parent * W + W), ends[0], ends[1]);
  const setup = { x: roundValue(b.x), y: roundValue(b.y), rotation: roundValue(b.rotation) };
  return { name: 'bone.create', input: { name, parent: ctx.maps.boneIds[parent] ?? '', length: roundValue(b.length), setup }, ids: [] };
}

/** Setup only (R-E1-14); a click (no drag) makes a zero-length bone; the new bone becomes the selection once committed. */
export const createBonePointer: ToolPointer = {
  press(ctx, p) {
    if (ctx.tool.mode !== 'setup') {
      ctx.notify({ kind: 'info', key: 'viewport.setupOnly' });
      return null;
    }
    const parent = topSelected(ctx, ctx.selection.bones).at(-1) ?? Math.max(0, ctx.data.bones.findIndex((b) => b.parent < 0));
    const name = slugifyName('bone', new Set(Object.values(ctx.doc.bones).map((x) => x.name)), 0).name;
    const head = snappedWorld(ctx, p, worldAt(ctx, p));
    const screenOf = (w: Vec2): Vec2 => viewCamera.worldToScreen(ctx.camera, w[0], w[1], [0, 0]);
    let tail = head;
    return {
      move(q) {
        tail = snappedWorld(ctx, q, worldAt(ctx, q));
        ctx.setGizmo({ handles: [], active: null, points: [], lines: [[screenOf(head), screenOf(tail)]] });
      },
      release: () => [createCall(ctx, parent, name, [head, tail])],
      committed(created) {
        const id = created[name];
        if (id !== undefined) updateSelection({ bones: [id], slots: [] }, 'replace');
      },
      cancel() {},
    };
  },
  idle: () => null,
};
